/*
 * TERMS-ACCEPTANCE-1 §B — scroll-to-accept Service Agreement clickwrap gate.
 *
 * For carts that contain a TUNE line item and have NOT yet accepted this
 * session, intercept the four checkout triggers ([name="checkout"]) and open
 * the agreement modal. Acceptance is recorded as cart attributes, then we
 * proceed natively to checkout.
 *
 * begin_checkout COORDINATION:
 *   ew-global.js wires capture-phase click+submit listeners on [name="checkout"]
 *   that fire begin_checkout. THIS module loads BEFORE ew-global.js (see
 *   layout/theme.liquid) so its capture-phase listener runs FIRST. On a gated
 *   click we preventDefault + stopImmediatePropagation, which both blocks the
 *   native checkout AND suppresses ew-global's premature begin_checkout. When we
 *   later proceed, we call form.requestSubmit(checkoutButton) (with a guard
 *   flag so OUR listener early-returns) — that fires a real submit event, so
 *   ew-global fires begin_checkout EXACTLY ONCE at the moment checkout actually
 *   proceeds.
 *
 * a11y: consumes global.js trapFocus / removeTrapFocus for focus containment;
 * Esc + Cancel close (cancelling checkout, not proceeding); focus returns to
 * the trigger on close. Scroll pane is keyboard-scrollable (tabindex="0").
 */
(function () {
  'use strict';

  /* ---- TUNE DISCRIMINATOR — single source of truth ----
     A cart line is a tune if its product type is the catalog tune type OR its
     variant id is one of the addon recalibration / full-tune parts. Mirrors the
     UNION used in order-printer/packing-slip.liquid. */
  var TUNE_PRODUCT_TYPE = 'ECU / DME';
  var TUNE_VARIANT_IDS = [42550313779235, 42550348185635];

  /* Cart attribute keys + version stamp for the recorded acceptance. */
  var ATTR_AGREEMENT = 'Tuning Agreement';
  var ATTR_VERSION = 'Tuning Agreement Version';
  var ATTR_ACCEPTED_UTC = 'Tuning Agreement Accepted (UTC)';
  var AGREEMENT_VALUE = 'Tuning Services and Liability Waiver Agreement';
  var AGREEMENT_VERSION = 'MW 7-4-25';

  /* Session-scoped accepted flag — once accepted this tab session, do not
     re-gate. (sessionStorage so a new visit re-prompts.) */
  var SESSION_KEY = 'ew_terms_accepted';

  /* Guard flag set right before we re-trigger the native submit so our own
     capture listener lets it through (and ew-global fires begin_checkout once). */
  var proceeding = false;

  var modal = null;
  var trigger = null;       // the checkout button the customer clicked
  var triggerForm = null;   // its owning form

  function $(sel, ctx) { return (ctx || document).querySelector(sel); }

  function isAcceptedThisSession() {
    try { return sessionStorage.getItem(SESSION_KEY) === '1'; } catch (_) { return false; }
  }
  function markAcceptedThisSession() {
    try { sessionStorage.setItem(SESSION_KEY, '1'); } catch (_) { /* private mode — no-op */ }
  }

  function cartHasTune(cart) {
    var lines = (cart && Array.isArray(cart.items)) ? cart.items : [];
    for (var i = 0; i < lines.length; i++) {
      var ln = lines[i] || {};
      if (ln.product_type === TUNE_PRODUCT_TYPE) return true;
      var vid = Number(ln.variant_id != null ? ln.variant_id : ln.id);
      if (TUNE_VARIANT_IDS.indexOf(vid) !== -1) return true;
    }
    return false;
  }

  /* Resolve the checkout button + its submitting form from an event target.
     The cart-page button (main-cart-items) is a submit button INSIDE
     form#ew-cart-form; the drawer/notification buttons live inside their own
     <form>; a button with form="..." resolves via .form. */
  function resolveTrigger(target) {
    var btn = target && target.closest ? target.closest('[name="checkout"]') : null;
    if (!btn) return null;
    var form = btn.form || (btn.closest ? btn.closest('form') : null);
    return { btn: btn, form: form };
  }

  /* Re-trigger native checkout: submit the original form via requestSubmit so a
     real submit event fires (ew-global hears it → one begin_checkout) and the
     browser performs the POST → /checkout. Falls back to .submit()/.click(). */
  function proceedNatively(form, btn) {
    proceeding = true;
    try {
      if (form && typeof form.requestSubmit === 'function') {
        form.requestSubmit(btn && btn.type === 'submit' ? btn : undefined);
      } else if (form && typeof form.submit === 'function') {
        form.submit();
      } else if (btn) {
        btn.click();
      }
    } finally {
      /* Release the guard shortly after; navigation usually unloads us first. */
      setTimeout(function () { proceeding = false; }, 1500);
    }
  }

  /* ---- modal plumbing ---- */

  function setStatus(text, scrolledEnd) {
    var status = $('[data-ew-terms-status]', modal);
    if (status) status.textContent = text;
    modal.classList.toggle('is-scrolled-end', !!scrolledEnd);
  }

  function showError(show) {
    var err = $('[data-ew-terms-error]', modal);
    if (err) err.hidden = !show;
  }

  /* FIX 1 — re-arm the focus trap after the focusable set changes (checkbox /
     Finish enabling). getFocusableElements excludes :disabled, so the trap's
     last boundary is stale until we rebuild it. Pass the current active element
     so focus isn't yanked back to the pane. trapFocus removes the old trap and
     recomputes first/last internally. */
  function rearmTrap(pane) {
    if (typeof trapFocus !== 'function' || !modal) return;
    var active = (document.activeElement && modal.contains(document.activeElement))
      ? document.activeElement
      : (pane || modal);
    trapFocus(modal, active);
  }

  /* FIX 3b — drive the scroll-progress rule (0→100%) from scroll position. */
  function updateProgress(pane) {
    var bar = $('[data-ew-terms-progress]', modal);
    if (!bar) return;
    var scrollable = pane.scrollHeight - pane.clientHeight;
    var pct = scrollable > 0 ? (pane.scrollTop / scrollable) * 100 : 100;
    if (pct < 0) pct = 0; if (pct > 100) pct = 100;
    bar.style.width = pct + '%';
  }

  /* FIX 6 — widen the scroll-end slack from 4 to 8px so iOS/Android momentum
     rounding still trips the unlock. */
  function atScrollEnd(pane) {
    return pane.scrollTop + pane.clientHeight >= pane.scrollHeight - 8;
  }

  function enableAcceptance() {
    var check = $('[data-ew-terms-check]', modal);
    var pane = $('[data-ew-terms-scroll]', modal);
    if (check && check.disabled) {
      check.disabled = false;
      setStatus('You can now agree and continue.', true); /* adds .is-scrolled-end */
      rearmTrap(pane); /* FIX 1 — checkbox is now focusable; rebuild boundaries */
    }
  }

  function syncFinish() {
    var check = $('[data-ew-terms-check]', modal);
    var finish = $('[data-ew-terms-finish]', modal);
    var pane = $('[data-ew-terms-scroll]', modal);
    if (finish) finish.disabled = !(check && check.checked);
    rearmTrap(pane); /* FIX 1 — Finish just enabled/disabled; rebuild last boundary */
  }

  function openModal() {
    if (!modal) return;
    showError(false);
    /* Reset gate state on each open (cart contents may have changed). */
    var check = $('[data-ew-terms-check]', modal);
    var finish = $('[data-ew-terms-finish]', modal);
    var pane = $('[data-ew-terms-scroll]', modal);
    if (check) { check.checked = false; check.disabled = true; }
    if (finish) finish.disabled = true;
    modal.classList.remove('is-scrolled-end');
    if (pane) pane.scrollTop = 0;
    var progress = $('[data-ew-terms-progress]', modal);
    if (progress) progress.style.width = '0%';

    modal.hidden = false;
    modal.removeAttribute('inert');
    document.body.style.overflow = 'hidden';

    /* FIX 8 — defer is-open to the next frame so the browser commits the
       hidden→shown layout first and the backdrop/panel enter transition plays
       on first open (otherwise the class is added in the same frame as unhide
       and the transition is skipped). */
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { modal.classList.add('is-open'); });
    });

    setStatus('Read the full agreement. Scroll to the end to continue.', false);

    document.addEventListener('keydown', onKeydown, true);

    /* Focus containment — consume global.js trapFocus. Focus the scroll pane
       so keyboard users can immediately read/scroll. (Trap is built here with
       the checkbox still disabled; rearmTrap rebuilds it as controls enable.) */
    if (typeof trapFocus === 'function') {
      trapFocus(modal, pane || modal);
    } else if (pane) {
      pane.focus();
    }

    /* If the agreement is already fully visible without scrolling (very tall
       viewport / short text), unlock immediately — AFTER the trap is set so
       enableAcceptance's rearmTrap rebuilds the boundaries with the checkbox
       focusable. */
    if (pane) {
      updateProgress(pane);
      if (atScrollEnd(pane)) enableAcceptance();
    }
  }

  function closeModal() {
    if (!modal) return;
    modal.classList.remove('is-open');
    modal.hidden = true;
    modal.setAttribute('inert', '');
    document.body.style.overflow = '';
    document.removeEventListener('keydown', onKeydown, true);

    /* Restore focus to the trigger. */
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(trigger && typeof trigger.focus === 'function' ? trigger : null);
    } else if (trigger && typeof trigger.focus === 'function') {
      trigger.focus();
    }
  }

  function onKeydown(e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      closeModal(); /* Esc cancels checkout — does NOT proceed. */
    }
  }

  /* Record acceptance as cart attributes, then proceed. Mirrors the CartNote
     fetch shape (cart.js): POST routes.cart_update_url with fetchConfig(). */
  function recordAndProceed() {
    var finish = $('[data-ew-terms-finish]', modal);
    if (finish) finish.disabled = true; /* prevent double-submit */
    showError(false);

    var attributes = {};
    attributes[ATTR_AGREEMENT] = AGREEMENT_VALUE;
    attributes[ATTR_VERSION] = AGREEMENT_VERSION;
    attributes[ATTR_ACCEPTED_UTC] = new Date().toISOString();

    var url = (window.routes && window.routes.cart_update_url) || '/cart/update.js';
    var cfg = (typeof fetchConfig === 'function')
      ? fetchConfig()
      : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };

    fetch(url, Object.assign({}, cfg, { body: JSON.stringify({ attributes: attributes }) }))
      .then(function (r) {
        if (!r.ok) throw new Error('cart update failed: ' + r.status);
        return r.json();
      })
      .then(function () {
        markAcceptedThisSession();
        var form = triggerForm, btn = trigger;
        closeModal();
        proceedNatively(form, btn); /* begin_checkout fires once here */
      })
      .catch(function () {
        /* Calm inline error — keep the modal open so the customer can retry. */
        showError(true);
        if (finish) finish.disabled = false;
      });
  }

  /* FIX 4 — authoritative /cart.js read with one retry, FAILING CLOSED.
     A tune cart must never reach checkout ungated, so if the read fails we do
     NOT proceed and do NOT open the modal (we can't confirm it's a tune cart);
     we surface a calm retry notice instead. Transient blips are absorbed by a
     single short retry. Resolves the parsed cart or rejects. */
  function readCart() {
    function attempt() {
      return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) {
        if (!r.ok) throw new Error('cart read failed: ' + r.status);
        return r.json();
      });
    }
    return attempt().catch(function () {
      return new Promise(function (resolve) { setTimeout(resolve, 400); })
        .then(attempt);
    });
  }

  /* FIX 4 — calm, retryable inline notice next to the checkout trigger when the
     authoritative cart read fails. One node per trigger; clears on next click.
     role="alert" so it's announced; uses the modal's error treatment. */
  function showCheckoutNotice(btn) {
    if (!btn || !btn.parentNode) return;
    var existing = btn.parentNode.querySelector('[data-ew-terms-checkout-notice]');
    if (!existing) {
      existing = document.createElement('p');
      existing.setAttribute('data-ew-terms-checkout-notice', '');
      existing.className = 'ew-terms-modal__error';
      existing.setAttribute('role', 'alert');
      btn.parentNode.insertBefore(existing, btn.nextSibling);
    }
    existing.hidden = false;
    /* Collective "we", no emoji, no exclamation points. */
    existing.textContent = 'We could not confirm your cart. Please try checking out again.';
  }
  function clearCheckoutNotice(btn) {
    if (!btn || !btn.parentNode) return;
    var n = btn.parentNode.querySelector('[data-ew-terms-checkout-notice]');
    if (n) n.hidden = true;
  }

  /* ---- the capture-phase interceptor ---- */

  /* Shared gate: clear any prior notice, read the cart (with retry), and route.
     On read failure FAIL CLOSED — surface the notice, do not proceed or open. */
  function gate(form, btn) {
    clearCheckoutNotice(btn);
    if (isAcceptedThisSession()) {
      proceedNatively(form, btn);
      return;
    }
    readCart()
      .then(function (cart) {
        if (!cartHasTune(cart)) {
          proceedNatively(form, btn);
          return;
        }
        trigger = btn;
        triggerForm = form;
        openModal();
      })
      .catch(function () {
        /* Fail CLOSED — a tune cart must not slip through ungated. */
        showCheckoutNotice(btn);
      });
  }

  function onCheckoutClick(e) {
    if (proceeding) return; /* our own re-trigger — let it through */
    var resolved = resolveTrigger(e.target);
    if (!resolved) return;

    /* Suppress native checkout AND ew-global's premature begin_checkout. */
    e.preventDefault();
    e.stopImmediatePropagation();

    gate(resolved.form, resolved.btn);
  }

  /* A submit may bubble up from a checkout form even without a click landing on
     the button (e.g. Enter). Intercept it the same way. */
  function onCheckoutSubmit(e) {
    if (proceeding) return; /* our own requestSubmit — let it through */
    var form = e.target;
    var btn = form && form.querySelector ? form.querySelector('[name="checkout"]') : null;
    if (!btn) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    gate(form, btn);
  }

  function wireModal() {
    modal = $('[data-ew-terms-modal]');
    if (!modal) return;

    var pane = $('[data-ew-terms-scroll]', modal);
    if (pane) {
      var onScroll = function () {
        updateProgress(pane); /* FIX 3b — drive the progress rule */
        if (atScrollEnd(pane)) enableAcceptance();
      };
      pane.addEventListener('scroll', onScroll);
      /* FIX 6 — momentum-scroll rounding on iOS/Android can leave the final
         `scroll` short of the threshold; scrollend + touchend re-check after
         the gesture settles so the unlock still trips. */
      pane.addEventListener('scrollend', onScroll);
      pane.addEventListener('touchend', onScroll);
    }

    var check = $('[data-ew-terms-check]', modal);
    if (check) check.addEventListener('change', syncFinish);

    var finish = $('[data-ew-terms-finish]', modal);
    if (finish) finish.addEventListener('click', recordAndProceed);

    Array.prototype.forEach.call(
      modal.querySelectorAll('[data-ew-terms-modal-close], [data-ew-terms-modal-backdrop]'),
      function (el) { el.addEventListener('click', closeModal); }
    );
  }

  function init() {
    wireModal();
    /* Capture phase + run BEFORE ew-global (guaranteed by load order). */
    document.addEventListener('click', onCheckoutClick, true);
    document.addEventListener('submit', onCheckoutSubmit, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
